import React, { useState, useEffect } from 'react';
import { useGallery } from '../context/GalleryContext';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FaSearch } from 'react-icons/fa';
import Gallery from '../components/Gallery';
import './Home.css';

const words = ["Inspiration", "Architecture", "Nature", "Art", "Minimalism"];

const heroImages = [
  'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // Event/Crowd (Existing)
  'https://images.unsplash.com/photo-1472214103451-9374bd1c798e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // Nature/Moody
  'https://images.unsplash.com/photo-1544531586-fde5298cdd40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // Art/Abstract
  'https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // MInimalism
  'https://images.unsplash.com/photo-1481437156560-3205f6a55735?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // Shopping/Vibrant
];

const Home = () => {
  const { searchImages, error } = useGallery();
  const [query, setQuery] = useState('');
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 200]);

  const [index, setIndex] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);

  // Word rotator
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Image rotator
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      searchImages(query);
    }
    window.scrollTo({
      top: 600,
      behavior: 'smooth'
    });
  };

  if (error) {
    return (
      <div className="error-container">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="home-container">
      <section className="hero-section">
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Discover{' '}
            <span className="dynamic-text">
              <AnimatePresence mode='wait'>
                <motion.span
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  style={{ display: 'inline-block' }}
                >
                  {words[index]}
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            A curated collection of breathtaking imagery from creators worldwide.
          </motion.p>

          <motion.form
            className="hero-search"
            onSubmit={handleSearch}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <input
              type="text"
              placeholder="Search for inspiration..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit">
              <FaSearch />
            </button>
          </motion.form>
        </div>

        {/* Dynamic Slideshow Background */}
        <motion.div className="hero-slideshow-container" style={{ y }}>
          <AnimatePresence mode='popLayout'>
            <motion.div
              key={currentImage}
              className="hero-slide"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              style={{
                backgroundImage: `url(${heroImages[currentImage]})`
              }}
            />
          </AnimatePresence>
          <div className="hero-overlay"></div>
        </motion.div>
      </section>

      <section className="gallery-section">
        <Gallery />
      </section>
    </div>
  );
};

export default Home;